import React, { Component } from 'react';
import { Icon, Menu, SemanticWIDTHS, Table } from 'semantic-ui-react';
import { strings } from '../..';

export interface Column<T> {
    key: keyof T,
    label: string,
    width?: SemanticWIDTHS,
    collapsing?: boolean,
    textAlign?: 'center' | 'left' | 'right',
    formatter?: (value: any, item: T) => string | JSX.Element
}

type DataTableProps<T> = {
    dataSource: ReadonlyArray<T>,
    primaryKey: keyof T,
    columns: ReadonlyArray<Column<T>>,
    page: number,
    itemsCount: number,
    pageSize: number,
    onSelect?: (row: T) => void,
    onPageChanged: (page: number) => void
}

type DataTableState = {
    selected: string,
}

export class DataTable<T> extends Component<DataTableProps<T>, DataTableState> {
    constructor(props: DataTableProps<T>) {
        super(props);

        this.state = {
            selected: ''
        };
    }

    render() {
        const { columns, dataSource } = this.props;
        return (
            <Table striped fixed size='small'>
                <Table.Header>
                    <Table.Row>
                        {columns.map(this.renderColumn)}
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {dataSource && dataSource.map(this.renderRow)}
                </Table.Body>
                {this.renderFooter()}
            </Table>);
    }

    onSelect = (row: T) => {
        const { primaryKey, onSelect } = this.props;
        const key = String(row[primaryKey]);

        this.setState({ selected: key });

        if (onSelect) {
            onSelect(row);
        }
    }

    getPageElement = (index: number) => {
        const { page } = this.props;
        const p = index + 1;

        return (
            <Menu.Item as="a" key={p} active={p === page} onClick={() => { this.pageChanged(p); }}>{p}</Menu.Item>
        );
    }

    getPageBreak = () => {
        return (<Menu.Item><Icon name="ellipsis horizontal" /></Menu.Item>);
    }

    pageChanged = (page: number) => {
        const { onPageChanged } = this.props;

        if (onPageChanged) {
            onPageChanged(page);
        }
    }

    renderPages = () => {
        const items = [];
        const pageCount = Math.ceil(this.props.itemsCount / this.props.pageSize);

        for (let index = 0; index < pageCount; index++) {
            items.push(this.getPageElement(index));
        }

        return (
            <Menu.Menu>
                {items}
            </Menu.Menu>
        );
    }

    renderFooter = () => {
        const { itemsCount, pageSize, columns } = this.props;
        return (
            <Table.Footer>
                <Table.Row>
                    <Table.HeaderCell colSpan={columns.length} style={{ backgroundColor: '#F2F2F2' }}>
                        <Menu>
                            {itemsCount > pageSize && this.renderPages()}
                            <Menu.Menu position='right'>
                                <Menu.Item onClick={() => window.scrollTo(0, 0)} style={{ width: 180 }}>
                                    <Icon name='arrow up' />
                                    {strings.core.dataTable.tornaSu}
                                </Menu.Item>
                            </Menu.Menu>
                        </Menu>
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Footer>
        );
    }

    renderCell = (row: T, column: Column<T>) => {
        let cell: string | JSX.Element = String(row[column.key]);
        if (column.formatter !== undefined) {
            cell = column.formatter!(row[column.key], row);
        }

        return (
            <Table.Cell key={String(column.key)} collapsing={column.collapsing} textAlign={column.textAlign}>
                {cell}
            </Table.Cell>
        );
    }

    renderRow = (row: T, index: number) => {
        const { columns, primaryKey } = this.props;
        const primaryKeyValue = String(row[primaryKey]);
        return (
            <Table.Row
                className="table-row"
                positive={primaryKeyValue === this.state.selected}
                onClick={() => this.onSelect(row)}
                key={index}>
                {columns.map(column => this.renderCell(row, column))}
            </Table.Row>);
    }

    renderColumn = (column: Column<T>, index: number) => {
        return (
            <Table.HeaderCell key={index} width={column.width} collapsing={column.collapsing} textAlign={column.textAlign} style={{ backgroundColor: '#F2F2F2' }}>
                {column.label}
            </Table.HeaderCell>
        );
    }
}
